import React, { useEffect, useState, useCallback } from "react";
import IntegrationsService from "../../services/Integrations/IntegrationsService";

// ── level config ──────────────────────────────────────────────────────────────

const LEVEL = {
    critical: {
        label: "Critical",
        badge: { background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" },
        icon: { background: "#fef2f2", color: "#dc2626" },
        border: "1px solid #fecaca",
        svg: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
        ),
    },
    warning: {
        label: "Warning",
        badge: { background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a" },
        icon: { background: "#fffbeb", color: "#d97706" },
        border: "1px solid #fde68a",
        svg: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
        ),
    },
    info: {
        label: "Info",
        badge: { background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe" },
        icon: { background: "#f5f3ff", color: "#7c3aed" },
        border: "1px solid #ddd6fe",
        svg: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
        ),
    },
};

// ── skeleton loader ───────────────────────────────────────────────────────────

function SkeletonCard() {
    return (
        <div style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: "18px 20px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
        }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: "#f3f4f6" }} />
                    <div style={{ width: 160, height: 14, borderRadius: 6, background: "#f3f4f6" }} />
                </div>
                <div style={{ width: 60, height: 22, borderRadius: 20, background: "#f3f4f6" }} />
            </div>
            <div style={{ width: "90%", height: 12, borderRadius: 6, background: "#f3f4f6" }} />
            <div style={{ width: "70%", height: 12, borderRadius: 6, background: "#f3f4f6" }} />
        </div>
    );
}

// ── insight card ──────────────────────────────────────────────────────────────

function InsightCard({ level, title, description }) {
    const cfg = LEVEL[level] || LEVEL.info;
    return (
        <div style={{
            background: "#fff",
            border: cfg.border,
            borderRadius: 12,
            padding: "18px 20px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
        }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 8,
                        background: cfg.icon.background, color: cfg.icon.color,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                    }}>
                        {cfg.svg}
                    </div>
                    <span style={{ fontWeight: 700, fontSize: 13, color: "#111827", lineHeight: 1.3 }}>
                        {title}
                    </span>
                </div>
                <span style={{
                    ...cfg.badge,
                    fontSize: 11, fontWeight: 600, borderRadius: 20,
                    padding: "3px 10px", whiteSpace: "nowrap", flexShrink: 0,
                }}>
                    {cfg.label}
                </span>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: "#6b7280", lineHeight: 1.6, paddingLeft: 46 }}>
                {description}
            </p>
        </div>
    );
}

// ── main component ────────────────────────────────────────────────────────────

export default function AiOperationalInsights() {
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const svc = new IntegrationsService();
            const res = await svc.zohoProjectsInsights();
            setInsights(res.insights || []);
        } catch (e) {
            console.error("[AiOperationalInsights] fetch error", e);
            setError("Could not load insights.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #eef0f3",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            padding: 24,
            marginBottom: 24,
        }}>
            {/* header */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 8,
                        background: "#f0f4ff", color: "#4f6ef7",
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                        </svg>
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>AI Operational Insights</div>
                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>
                            Key delivery risks and bottlenecks identified by AI analysis.
                        </div>
                    </div>
                </div>
            </div>

            {/* cards grid */}
            {error ? (
                <div style={{ color: "#dc2626", fontSize: 12, padding: "12px 0" }}>{error}</div>
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {loading
                        ? [0, 1, 2, 3].map((i) => <SkeletonCard key={i} />)
                        : insights.length === 0
                            ? <div style={{ gridColumn: "1/-1", color: "#9ca3af", fontSize: 12, textAlign: "center", padding: 16 }}>No insights available.</div>
                            : insights.map((insight, i) => (
                                <InsightCard key={i} level={insight.level} title={insight.title} description={insight.description} />
                            ))
                    }
                </div>
            )}
        </div>
    );
}
